import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

const scrollThreshold = 200;
const scrollKeys = new Set(['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' ']);

export function useGuestPromoScroll(enabled: boolean) {
  const [hasScrolled, setHasScrolled] = useState(false);
  const { events } = useRouter();

  useEffect(() => {
    if (!enabled || hasScrolled) return;

    let distance = 0;
    let previousY = window.scrollY;
    let lastInputAt: number | null = null;

    function recordInput() {
      lastInputAt = performance.now();
    }

    function onWheel(event: WheelEvent) {
      if (event.deltaY !== 0) recordInput();
    }

    function onKeyDown(event: KeyboardEvent) {
      if (!scrollKeys.has(event.key) || event.defaultPrevented) return;
      if (
        event.target instanceof Element &&
        event.target.closest('input, textarea, select, [contenteditable], [role="textbox"]')
      ) {
        return;
      }
      recordInput();
    }

    function onScroll() {
      const delta = Math.abs(window.scrollY - previousY);
      previousY = window.scrollY;

      // Page navigation and history restoration also emit scroll events.
      // Count only page movement following a scroll gesture or scroll key.
      if (
        lastInputAt === null ||
        performance.now() - lastInputAt > 1000 ||
        document.body.classList.contains('overflow-hidden')
      ) {
        return;
      }

      distance += delta;
      if (distance < scrollThreshold) return;

      cleanup();
      setHasScrolled(true);
    }

    function reset() {
      distance = 0;
      previousY = window.scrollY;
      lastInputAt = null;
    }

    function cleanup() {
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchmove', recordInput);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScroll);
      events.off('routeChangeStart', reset);
    }

    window.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('touchmove', recordInput, { passive: true });
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScroll, { passive: true });
    events.on('routeChangeStart', reset);

    return cleanup;
  }, [enabled, events, hasScrolled]);

  return hasScrolled;
}
